import {
  DataTableViewOptions,
  simpleDateColumn,
  simpleTextColumn,
} from "@/components/DataTable";
import { ConfirmActionDialog } from "@/components/Dialog/ConfirmActionDialog";
import PopOver from "@/components/PopOver";
import { toast } from "@/components/ui/use-toast";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import { handleApiError } from "@/lib/api/error";
import { defaultNS } from "@/lib/i18n";
import { queryClient } from "@/lib/queryClient";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@radix-ui/react-dropdown-menu";
import { useMutation } from "@tanstack/react-query";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CertificationsEditDialog from "../-components/CertificationsEditDialog";
import { Columns } from "../-constants";
import { deleteCertification } from "../-query/api";
import { certificationsKeys } from "../-query/key";
import type { GetCertificationsListRes } from "../-query/type";

type CertificationsTableRow = GetCertificationsListRes["items"][number];

// APIデータをPersonCell期待形式に変換
export type TransformedCertificationsTableRow = Omit<
  CertificationsTableRow,
  "certificationHolder"
> & {
  certificationHolder: {
    id: string;
    name: string;
    namePronunciation: string;
    avatarUrl: string;
  };
};

export function useCertificationsColumns(): ColumnDef<TransformedCertificationsTableRow>[] {
  return useMemo(() => {
    const columns = [
      simpleTextColumn(Columns.CERTIFICATION_ID, {
        i18nKey: "educationsAndQualifications.certificationId",
        minWidth: "150px",
        sortable: true,
        enableHiding: true,
      }),
      simpleTextColumn(Columns.CERTIFICATION_NAME, {
        i18nKey: "educationsAndQualifications.certificationName",
        minWidth: "240px",
        sortable: true,
        enableHiding: true,
      }),
      simpleTextColumn(Columns.CERTIFICATION_PARTY, {
        i18nKey: "educationsAndQualifications.certificationParty",
        minWidth: "240px",
        sortable: true,
        enableHiding: true,
      }),
      simpleDateColumn(Columns.EFFECTIVE_DATE, "date", {
        i18nKey: "educationsAndQualifications.effectiveDate",
        minWidth: "120px",
        sortable: true,
        enableHiding: true,
      }),
      simpleDateColumn(Columns.EXPIRATION_DATE, "date", {
        i18nKey: "educationsAndQualifications.expirationDate",
        minWidth: "120px",
        sortable: true,
        enableHiding: true,
      }),
      simpleDateColumn(Columns.RENEWAL_DATE, "date", {
        i18nKey: "educationsAndQualifications.renewalDate",
        minWidth: "120px",
        sortable: true,
        enableHiding: true,
      }),
    ] satisfies ColumnDef<TransformedCertificationsTableRow>[];

    // TODO: 台帳側での表示は未実装のため一旦コメントアウト
    // if (personId) {
    //   columns.push(
    //     personColumn(Columns.CERTIFICATION_HOLDER, {
    //       i18nKey: "educationsAndQualifications.certificationHolder",
    //       minWidth: "240px",
    //       sortable: true,
    //       enableHiding: true,
    //     }),
    //   );
    // }

    columns.push(ActionsColumn("actions"));

    return columns;
  }, []);
}

/** アクションを表示するカラム */
export function ActionsColumn(
  id = "actions",
): ColumnDef<TransformedCertificationsTableRow> {
  return {
    accessorKey: id,
    enableSorting: false,
    header: ({ table }) => (
      <div className="flex justify-end pr-2">
        <DataTableViewOptions table={table} />
      </div>
    ),
    cell: ({ row }) => <ActionsCell row={row} />,
  };
}

function ActionsCell({ row }: { row: Row<TransformedCertificationsTableRow> }) {
  const rowData: TransformedCertificationsTableRow = row.original;

  const { i18n } = useTranslation();
  const tc = useCommonTranslation();
  const { t } = useTranslation(defaultNS, {
    keyPrefix:
      "pages.person.profile.educationsAndQualifications.certification.dialog",
  });

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // useMutationを使用して削除処理を実行
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteCertification(id),
    onSuccess: () => {
      // 削除成功後、certificationsのキャッシュを無効化してデータを再取得
      queryClient.invalidateQueries({
        queryKey: certificationsKeys.all,
      });
      setDeleteDialogOpen(false);
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      toast({ title: message });
    },
  });

  const handleDelete = async () => {
    await deleteMutation.mutateAsync(rowData.id);
  };

  return (
    <div className="flex justify-end pr-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <PopOver
            options={[
              {
                label: tc("action.edit"),
                disabled: false,
                action: () => setEditDialogOpen(true),
              },
              {
                label: tc("action.delete"),
                disabled: false,
                action: () => setDeleteDialogOpen(true),
              },
            ]}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[160px]" />
      </DropdownMenu>
      <ConfirmActionDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        okLabel={tc("action.permanentlyDelete")}
        okVariant="destructive"
        title={t("delete.title", {
          resource: rowData.certificationName,
        })}
        description={t("delete.description", {
          resource: rowData.certificationName,
        })}
      />
      <CertificationsEditDialog
        isOpen={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        data={{
          id: rowData.id,
          certificationHolderId: rowData.certificationHolder.id,
          certificationId: rowData.certificationId,
          certificationName: rowData.certificationName,
          certificationParty: rowData.certificationParty,
          effectiveDate: rowData.effectiveDate,
          expirationDate: rowData.expirationDate,
          renewalDate: rowData.renewalDate,
        }}
      />
    </div>
  );
}
